// Lógica pura del cuadrante de pistas (sin llamadas a red): derivar franjas
// de 30 min de la disponibilidad real (no continua), detectar conflicto de
// jugador y proponer un reparto sin conflicto. Ver docs/PLAN_COMPETICION_PISTAS.md §3/§6.

import { nombreVisible } from '../../utils/jugadores';

export const DURACION_FRANJA_MIN = 30;

// Nombres reales de modalidad tal como los devuelve badminton-api (mayúsculas
// en BD, ver src/components/Jornadas.js). El orden es el de rotación de
// "Proponer" cuando no hay jornada anterior de la que partir.
export const MODALIDADES = [
  'DOBLES FEMENINO',
  'DOBLES MASCULINO',
  'DOBLES MIXTO',
  'INDIVIDUAL FEMENINO',
  'INDIVIDUAL MASCULINO',
];

export const capitalizarModalidad = (m) => (m ? m.charAt(0) + m.slice(1).toLowerCase() : m);

const horaAMinutos = (hora) => {
  const [h, m] = hora.split(':').map(Number);
  return h * 60 + m;
};
const minutosAHora = (min) => {
  const h = String(Math.floor(min / 60)).padStart(2, '0');
  const m = String(min % 60).padStart(2, '0');
  return `${h}:${m}`;
};

// A partir de los tramos reservados (posiblemente no continuos ni iguales
// entre pistas), calcula las franjas de 30 min que hay que pintar como filas
// y qué pistas están disponibles en cada una.
export function derivarFranjas(disponibilidad, duracionMin = DURACION_FRANJA_MIN) {
  const inicios = new Set();
  disponibilidad.forEach((tramo) => {
    const inicio = horaAMinutos(tramo.hora_inicio);
    const fin = horaAMinutos(tramo.hora_fin);
    for (let t = inicio; t + duracionMin <= fin; t += duracionMin) inicios.add(t);
  });

  return [...inicios].sort((a, b) => a - b).map((inicioMin) => {
    const finMin = inicioMin + duracionMin;
    const pistasDisponibles = [...new Set(
      disponibilidad
        .filter((t) => horaAMinutos(t.hora_inicio) <= inicioMin && horaAMinutos(t.hora_fin) >= finMin)
        .map((t) => t.pista)
    )].sort((a, b) => a - b);
    return { id: minutosAHora(inicioMin), hora: minutosAHora(inicioMin), horaFin: minutosAHora(finMin), pistasDisponibles };
  });
}

// Convierte los partidos tal como los devuelve GET .../pistas (con nombres
// de participante, sin jugadores individuales) al modelo que pintan
// PartidoCard/Celda/FilaFranja, cruzando con el roster de
// useCalendarioDetalle (participante_id -> jugadores) para poder detectar
// conflicto de jugador entre modalidades.
// `adelantables`: la lista son partidos de otra jornada aún sin colocar, así que su
// banner de jornada se pinta siempre.
export function mapearPartidos(partidosApi, rosterPorParticipante, { adelantables = false } = {}) {
  return partidosApi.map((p) => {
    // local_nombre/visitante_nombre son v_participantes.nombre_mostrar: el
    // nombre de equipo elegido por los jugadores en dobles (no sus nombres),
    // y el nombre de la persona en individual. Para poder ver quién juega
    // en dobles hace falta el roster aparte.
    // Se muestra el alias si lo hay, pero el conflicto de jugador se detecta
    // por nombre completo (`jugadores`): dos personas con el mismo alias no
    // deben contar como la misma.
    const rosterLocal = rosterPorParticipante[p.local_participante_id] || [];
    const rosterVisitante = rosterPorParticipante[p.visitante_participante_id] || [];
    const jugadoresLocal = rosterLocal.map(nombreVisible);
    const jugadoresVisitante = rosterVisitante.map(nombreVisible);
    return {
      id: p.id,
      modalidad: p.modalidad,
      // Una jornada real puede mezclar IDA de una modalidad con VUELTA de
      // otra (roundRobin.ts no reinicia la numeración en la vuelta), así que
      // conviene distinguirlo en la tarjeta.
      tipo: p.tipo,
      local: p.local_nombre,
      visitante: p.visitante_nombre,
      jugadoresLocal,
      jugadoresVisitante,
      jugadores: [...rosterLocal, ...rosterVisitante].map((j) => j.nombre),
      jugadoresVisibles: [...rosterLocal, ...rosterVisitante].map((j) => ({ nombre: j.nombre, visible: nombreVisible(j) })),
      pista: p.pista,
      franjaId: p.hora_inicio,
      jornada: p.jornada,
      // Jornada propia del partido si ocupa un cuadrante que no es el suyo (adelantado).
      jornadaOrigen: adelantables || (p.jornada_cuadrante != null && p.jornada !== p.jornada_cuadrante) ? p.jornada : null,
      // Ya jugado (resultado propuesto/confirmado o con fecha de juego): no debe entrar en ningún cuadrante.
      yaJugado: !!p.jugado || p.estado === 'PROPUESTO' || p.estado === 'CONFIRMADO',
    };
  });
}

// Igual que en el boceto (Fase 0): por franja, qué partidos comparten
// jugador aunque estén en pistas distintas.
export function computeConflictos(partidos) {
  const porFranja = {};
  partidos.forEach((p) => {
    if (!p.franjaId) return;
    (porFranja[p.franjaId] = porFranja[p.franjaId] || []).push(p);
  });

  const celdas = {};
  const marcar = (partido, otro, jugador) => {
    const key = `${partido.pista}-${partido.franjaId}`;
    (celdas[key] = celdas[key] || []).push({ jugador, contraPartido: otro.id });
  };

  Object.values(porFranja).forEach((lista) => {
    for (let i = 0; i < lista.length; i++) {
      for (let j = i + 1; j < lista.length; j++) {
        const a = lista[i];
        const b = lista[j];
        a.jugadores.filter((nombre) => b.jugadores.includes(nombre)).forEach((nombre) => {
          marcar(a, b, nombre);
          marcar(b, a, nombre);
        });
      }
    }
  });

  return celdas;
}

// Reparto automático: coloca los partidos pendientes de la jornada sin
// conflicto de jugador, empezando por una modalidad distinta a la que abrió
// la jornada anterior, y priorizando llenar una franja entera con la misma
// modalidad. No decide qué hacer si no caben todos (§6, paso 4 — modal de
// prioridad) — de momento solo informa de los que se quedan sin hueco.
export function proponerReparto(partidos, franjas, modalidadAnterior) {
  const colocados = partidos.filter((p) => p.franjaId);
  const pendientes = partidos.filter((p) => !p.franjaId);

  const idxAnterior = Math.max(MODALIDADES.indexOf(modalidadAnterior), -1);
  const ordenModalidades = [
    ...MODALIDADES.slice(idxAnterior + 1),
    ...MODALIDADES.slice(0, idxAnterior + 1),
  ];
  const pendientesOrdenados = ordenModalidades
    .map((modalidad) => pendientes.filter((p) => p.modalidad === modalidad))
    .flat()
    // por si algún partido tiene una modalidad fuera de la lista conocida
    .concat(pendientes.filter((p) => !ordenModalidades.includes(p.modalidad)));

  const ocupadas = new Set(colocados.map((p) => `${p.pista}-${p.franjaId}`));
  const jugadoresPorFranja = {};
  colocados.forEach((p) => {
    const set = (jugadoresPorFranja[p.franjaId] = jugadoresPorFranja[p.franjaId] || new Set());
    p.jugadores.forEach((j) => set.add(j));
  });

  const huecos = [];
  franjas.forEach((f) => f.pistasDisponibles.forEach((pista) => {
    const key = `${pista}-${f.id}`;
    if (!ocupadas.has(key)) huecos.push({ pista, franjaId: f.id });
  }));

  const resultado = [...colocados];
  const sinSitio = [];

  pendientesOrdenados.forEach((partido) => {
    const hueco = huecos.find((h) => {
      const key = `${h.pista}-${h.franjaId}`;
      if (ocupadas.has(key)) return false;
      const jugadoresFranja = jugadoresPorFranja[h.franjaId] || new Set();
      return !partido.jugadores.some((j) => jugadoresFranja.has(j));
    });

    if (hueco) {
      ocupadas.add(`${hueco.pista}-${hueco.franjaId}`);
      const set = (jugadoresPorFranja[hueco.franjaId] = jugadoresPorFranja[hueco.franjaId] || new Set());
      partido.jugadores.forEach((j) => set.add(j));
      resultado.push({ ...partido, pista: hueco.pista, franjaId: hueco.franjaId });
    } else {
      sinSitio.push(partido);
      resultado.push(partido);
    }
  });

  return { partidos: resultado, sinSitio, modalidadElegida: ordenModalidades[0] };
}
